import Vue from 'vue';
import { TM_API_ENDPOINTS, TM_MESSAGES, isDownloadInProgress, isDownloadFailed } from '../utils/constants';

const $axios = Vue.axios;

export default {
  namespaced: true,
  state: {
    loading: false,
    analysisDetails: {},
    analysisLoading: false,
    downloadHistory: [],
    loadingExcelHistory: false,
    middlewareValues: [],
    middlewareLoading: false,
    filterList: [],
    filterLoading: false,
    botDetails: {},
    botId: '',
    metadata: {},
    formData: { analysisTypeItems: [], fileTypeItems: [], results: [] },
    analysisList: [],
    /** Last applied filter tab per collection: 'saved' | 'playground' */
    lastActiveFilterTabByCollection: {},
  },

  getters: {
    isLoading: state => state.loading,
    analysisDetails: state => state.analysisDetails,
    isAnalysisLoading: state => state.analysisLoading,
    downloadHistory: state => state.downloadHistory,
    isLoadingExcelHistory: state => state.loadingExcelHistory,
    middlewareValues: state => state.middlewareValues,
    isMiddlewareLoading: state => state.middlewareLoading,
    filterList: state => state.filterList,
    isFilterLoading: state => state.filterLoading,
    botDetails: state => state.botDetails,
    botId: state => state.botId,
    metadata: state => state.metadata,
    formData: state => state.formData,
    analysisList: state => state.analysisList,
    lastActiveFilterTab: state => collection => {
      if (!collection) return null;
      return state.lastActiveFilterTabByCollection[collection] ?? null;
    },
  },

  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ANALYSIS_DETAILS(state, details) {
      state.analysisDetails = details;
    },
    SET_ANALYSIS_LOADING(state, loading) {
      state.analysisLoading = loading;
    },
    SET_DOWNLOAD_HISTORY(state, history) {
      state.downloadHistory = history;
    },
    SET_LOADING_EXCEL_HISTORY(state, loading) {
      state.loadingExcelHistory = loading;
    },
    SET_MIDDLEWARE_VALUES(state, values) {
      state.middlewareValues = values;
    },
    SET_MIDDLEWARE_LOADING(state, loading) {
      state.middlewareLoading = loading;
    },
    SET_FILTER_LIST(state, filters) {
      state.filterList = filters;
    },
    SET_FILTER_LOADING(state, loading) {
      state.filterLoading = loading;
    },
    SET_BOT_DETAILS(state, details) {
      state.botDetails = details;
    },
    SET_BOT_ID(state, id) {
      state.botId = id;
    },
    SET_METADATA(state, metadata) {
      state.metadata = metadata;
    },
    SET_FORM_DATA(state, data) {
      state.formData = data;
    },
    SET_ANALYSIS_LIST(state, list) {
      state.analysisList = list;
    },
    SET_LAST_ACTIVE_FILTER_TAB(state, { collection, tab }) {
      if (!collection) return;
      if (tab === null || tab === undefined) {
        Vue.delete(state.lastActiveFilterTabByCollection, collection);
      } else {
        Vue.set(state.lastActiveFilterTabByCollection, collection, tab);
      }
    },
  },

  actions: {
    async fetchAnalysisDetails({ commit }, { id, anaid }) {
      if (!id || !anaid) {
        console.error('[TransactionMatchingStore]: fetchAnalysisDetails - missing id or anaid');
        commit('SET_ANALYSIS_DETAILS', {});
        return {};
      }

      commit('SET_ANALYSIS_LOADING', true);
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.GET_TM_ANALYSIS_DETAILS}/${id}/${anaid}`);
        const resData = response?.data || {};
        const payload = resData?.data;

        let analysisDetails = {};
        if (resData.status === 'success') {
          if (Array.isArray(payload)) {
            analysisDetails = payload[0] || {};
          } else if (payload && typeof payload === 'object') {
            // Some endpoints may wrap the array/object under payload.data / payload.analysisDetails
            if (Array.isArray(payload.data)) {
              analysisDetails = payload.data[0] || {};
            } else if (payload.analysisDetails && typeof payload.analysisDetails === 'object') {
              analysisDetails = payload.analysisDetails;
            } else {
              analysisDetails = payload;
            }
          }
        }

        // TM APIs return this as `actiondetails`; normalize to `analysisDetails.actiondetails`
        // (keep minimal fallback for cases where it comes alongside the payload object)
        const payloadActionDetails =
          (!Array.isArray(payload) && payload && typeof payload === 'object') ? payload.actiondetails : undefined;

        const actiondetails = analysisDetails.actiondetails ?? resData.actiondetails ?? payloadActionDetails;

        if (analysisDetails.actiondetails == null && actiondetails != null) {
          analysisDetails = { ...analysisDetails, actiondetails };
        }

        commit('SET_ANALYSIS_DETAILS', analysisDetails);
        return analysisDetails;
      } catch (err) {
        commit('SET_ANALYSIS_DETAILS', {});
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error('[TransactionMatchingStore]: fetchAnalysisDetails error', err);
        return {};
      } finally {
        commit('SET_ANALYSIS_LOADING', false);
      }
    },

    async generateResult({ commit, dispatch }, { item, botId, tables, dataFilters, collection }) {
      commit('SET_LOADING', true);
      const payloadEntry = { section: item };
      if (tables && tables.length > 0) {
        payloadEntry.tables = tables;
      }
      if (dataFilters && dataFilters.length > 0) {
        payloadEntry.dataFilters = dataFilters;
      }
      if (collection) {
        payloadEntry.collection = collection;
      }
      const dataPayload = [payloadEntry];

      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.GENERATE_RESULT}/${botId}`, { data: dataPayload });

        if (response.data.status === "success") {
          const resultData = response.data.data[0] || {};
          
          // Trigger download check immediately as per original logic
          await dispatch('downloadResult', { item, botId });

          if (isDownloadInProgress(resultData.stage)) {
            commit('sbSuccess', TM_MESSAGES.FILE_GENERATING, { root: true });
          } else if (isDownloadFailed(resultData.stage)) {
            commit('sbError', TM_MESSAGES.ERROR_GENERATING, { root: true });
          } else if (resultData.upload) {
            // Return the upload path so component can handle the actual download
            // or we could use Vue.prototype.$nova if available in store context via helper
            if (Vue.prototype.$nova) {
              Vue.prototype.$nova.downloadFile(resultData.upload);
            }
            return resultData.upload;
          } else {
             // "no file found to download" - as in original code, maybe return null
             return null;
          }
        } else {
          throw new Error(response.data.message || TM_MESSAGES.UNKNOWN_ERROR);
        }
      } catch (err) {
        commit('sbError', err.message || err || TM_MESSAGES.UNKNOWN_ERROR, { root: true });
        console.error('[TransactionMatchingStore]: downloadResult error', err);
        throw err; // Propagate error if component needs to know
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async downloadResult({ commit }, { item, botId }) {
      commit('SET_LOADING', true);
      const dataPayload = [{ section: item }];

      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.GET_RECENT_DOWNLOADS}/${botId}`, { data: dataPayload });

        if (response.data.status === "success") {
          const resultData = response.data.data[0] || {};

          if (isDownloadInProgress(resultData.stage)) {
             commit('sbSuccess', TM_MESSAGES.FILE_GENERATING, { root: true });
          } else if (isDownloadFailed(resultData.stage)) {
             commit('sbError', TM_MESSAGES.ERROR_GENERATING, { root: true });
          } else if (resultData.upload) {
            if (Vue.prototype.$nova) {
              Vue.prototype.$nova.downloadFile(resultData.upload);
            }
            return resultData.upload;
          } else {
             return null;
          }
        } else {
          throw new Error(response.data.message || TM_MESSAGES.UNKNOWN_ERROR);
        }
      } catch (err) {
        commit('sbError', err.message || err || TM_MESSAGES.UNKNOWN_ERROR, { root: true });
        console.error(err);
        throw err;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Bulk manual match for two-file transaction matching.
     * Uploads files and sends them for consolidated manual matching.
     * @param {Object} payload - { uploadData, tmaId, runId, clientCode }
     */
    async bulkManualMatchTwoFile({ commit }, { uploadData, tmaId, runid, clientCode }) {
      try {
        const data = {
          upload: uploadData,
          tmaid: tmaId,
          runid: runid,
          clientCode: clientCode?.toString() || '',
        };
        const response = await $axios.post(TM_API_ENDPOINTS.TWO_FILES_BULK_MANUAL_MATCH, data);
        if (response.data.status === 'success') {
          commit('sbSuccess', TM_MESSAGES.BULK_MANUAL_MATCH_SUCCESS, { root: true });
          return response.data;
        } else {
          throw new Error(response.data.message || TM_MESSAGES.UNKNOWN_ERROR);
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: bulkManualMatchTwoFile error', err);
        commit('sbError', err.message || err || TM_MESSAGES.UNKNOWN_ERROR, { root: true });
        throw err;
      } 
    },

    async getMiddlewareList({ commit }, { pid, tmaid, bpid }) {
      if (!pid || !tmaid || !bpid) {
        console.error('[TransactionMatchingStore]: getMiddlewareList — missing params');
        return [];
      }
      commit('SET_MIDDLEWARE_LOADING', true);
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.GET_TM_MIDDLEWARE}/${pid}/${tmaid}/${bpid}`);
        if (response.data.status === 'success' && response.data.data.length > 0) {
          commit('SET_MIDDLEWARE_VALUES', response.data.data);
          return response.data.data;
        } else {
          commit('SET_MIDDLEWARE_VALUES', []);
          return [];
        }
      } catch (err) {
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error('[TransactionMatchingStore]: getMiddlewareList error', err);
        return [];
      } finally {
        commit('SET_MIDDLEWARE_LOADING', false);
      }
    },

    async addMiddleware({ commit }, { middlewareData }) {
      try {
        const response = await $axios.post(TM_API_ENDPOINTS.ADD_TM_MIDDLEWARE, { data: middlewareData });
        if (response.data.status === 'success') {
          commit('sbSuccess', 'Mapping master successfully saved', { root: true });
          return { status: 'success' };
        } else {
          if (response.data.data?.length > 0 && response.data.data[0].index0) {
            return { status: 'validation_error', errors: response.data.data[0].index0 };
          }
          throw new Error(response.data.message || 'Failed to add middleware');
        }
      } catch (err) {
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error('[TransactionMatchingStore]: addMiddleware error', err);
        throw err;
      }
    },

    async updateMiddleware({ commit }, { id, middlewareData }) {
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.UPDATE_TM_MIDDLEWARE}/${id}`, { data: middlewareData });
        if (response.data.status === 'success') {
          commit('sbSuccess', 'Mapping master successfully saved', { root: true });
          return { status: 'success' };
        } else {
          if (response.data.data?.length > 0 && response.data.data[0].index0) {
            return { status: 'validation_error', errors: response.data.data[0].index0 };
          }
          throw new Error(response.data.message || 'Failed to update middleware');
        }
      } catch (err) {
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error('[TransactionMatchingStore]: updateMiddleware error', err);
        throw err;
      }
    },

    async deleteMiddleware({ commit }, { id }) {
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.DELETE_TM_MIDDLEWARE}/${id}`, {});
        if (response.data.status === 'success') {
          commit('sbSuccess', 'Mapping master successfully deleted', { root: true });
          return { status: 'success' };
        } else {
          throw new Error(response.data.message || 'Failed to delete middleware');
        }
      } catch (err) {
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error('[TransactionMatchingStore]: deleteMiddleware error', err);
        throw err;
      }
    },

    async enableMiddleware({ commit }, { id, action }) {
      const endpoint = action === 'Enable'
        ? TM_API_ENDPOINTS.ENABLE_TM_MIDDLEWARE
        : TM_API_ENDPOINTS.DISABLE_TM_MIDDLEWARE;
      try {
        const response = await $axios.post(`${endpoint}/${id}`, {});
        if (response.data.status === 'success') {
          commit('sbSuccess', `Mapping master successfully ${action.toLowerCase()}d`, { root: true });
          return { status: 'success' };
        } else {
          throw new Error(response.data.message || 'Error performing action');
        }
      } catch (err) {
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error('[TransactionMatchingStore]: enableMiddleware error', err);
        throw err;
      }
    },

    /**
     * Fetch filter list via tmfilter/get API.
     * Also triggers backend key generation for legacy data without keys.
     * @param {Object} payload - { bpid }
     * @returns {Array} Filter list
     */
    async getFilterList({ commit }, { bpid }) {
      if (!bpid) {
        console.error('[TransactionMatchingStore]: getFilterList — missing bpid');
        return [];
      }
      commit('SET_FILTER_LOADING', true);
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.GET_TM_FILTER}/${bpid}`);
        if (response.data.status === 'success' && response.data.data.length > 0) {
          commit('SET_FILTER_LIST', response.data.data);
          return response.data.data;
        } else {
          commit('SET_FILTER_LIST', []);
          return [];
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: getFilterList error', err);
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        return [];
      } finally {
        commit('SET_FILTER_LOADING', false);
      }
    },

    /**
     * Add or update a filter via upsert API.
     * @param {Object} payload - { bpid, filterData }
     */
    async upsertFilter({ commit }, { bpid, filterData }) {
      if (!bpid) {
        console.error('[TransactionMatchingStore]: upsertFilter — missing bpid');
        throw new Error('Missing bpid');
      }
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.UPSERT_TM_FILTER}/${bpid}`, { data: { filter: filterData } });
        if (response.data.status === 'success') {
          return { status: 'success' };
        } else {
          throw new Error(response.data.message || 'Failed to upsert filter');
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: upsertFilter error', err);
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Delete a filter via delete API.
     * @param {Object} payload - { bpid, key }
     */
    async deleteFilter({ commit }, { bpid, key }) {
      if (!bpid || !key) {
        console.error('[TransactionMatchingStore]: deleteFilter — missing bpid or key');
        throw new Error('Missing bpid or key');
      }
      try {
        const response = await $axios.delete(`${TM_API_ENDPOINTS.DELETE_TM_FILTER}/${bpid}/${key}`);
        if (response.data.status === 'success') {
          return { status: 'success' };
        } else {
          throw new Error(response.data.message || 'Failed to delete filter');
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: deleteFilter error', err);
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Toggle filter enabled/disabled status.
     * @param {Object} payload - { bpid, key, enable }
     */
    async toggleFilterStatus({ commit }, { bpid, key, enable }) {
      if (!bpid || !key) {
        console.error('[TransactionMatchingStore]: toggleFilterStatus — missing bpid or key');
        throw new Error('Missing bpid or key');
      }
      const endpoint = enable ? TM_API_ENDPOINTS.ENABLE_TM_FILTER : TM_API_ENDPOINTS.DISABLE_TM_FILTER;
      try {
        const response = await $axios.put(`${endpoint}/${bpid}`, { data: { filter: { key } } });
        if (response.data.status === 'success') {
          return { status: 'success' };
        } else {
          throw new Error(response.data.message || `Failed to ${enable ? 'enable' : 'disable'} filter`);
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: toggleFilterStatus error', err);
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Reorder filters via reorder API.
     * @param {Object} payload - { bpid, orderedKeys } where orderedKeys is [{ key }, ...]
     */
    async reorderFilters({ commit }, { bpid, orderedKeys }) {
      if (!bpid) {
        console.error('[TransactionMatchingStore]: reorderFilters — missing bpid');
        throw new Error('Missing bpid');
      }
      try {
        const response = await $axios.put(`${TM_API_ENDPOINTS.REORDER_TM_FILTER}/${bpid}`, { data: { filters: orderedKeys } });
        if (response.data.status === 'success') {
          return { status: 'success' };
        } else {
          throw new Error(response.data.message || 'Failed to save filter order');
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: reorderFilters error', err);
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        throw err;
      }
    },

    async fetchDownloadHistory({ commit }, botId) {
      commit('SET_LOADING_EXCEL_HISTORY', true);
      // Removed "data: ..." payload as original code sent empty object {} in axios post body for this call
      // Wait, original code: this.axios.post("...", {})
      // My original store code: data: [{ section: 'Excel history' }] - Maybe this was for generate/downloadResult
      // But fetchDownloadHistory used empty object in component: this.axios.post(..., {})
      
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.GET_RECENT_DOWNLOADS}/${botId}`, {}); // Empty body
        if (response.data.status === 'success') {
             let files = response.data.data || [];
             // Sort by created_at descending and take latest 5
             files = files
               .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
               .slice(0, 5);
             commit('SET_DOWNLOAD_HISTORY', files);
        } else {
             commit('SET_DOWNLOAD_HISTORY', []);
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: fetchDownloadHistory error', err);
        commit('SET_DOWNLOAD_HISTORY', []);
      } finally {
        commit('SET_LOADING_EXCEL_HISTORY', false);
      }
    },

    /**
     * Fetch bot details by project id
     */
    async fetchBotDetails({ commit }, { id }) {
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.GET_BOT_DETAILS}/${id}`);
        if (response.data.status === 'success' && response.data.data.length > 0) {
          const data = response.data.data[0] || {};
          commit('SET_METADATA', data.metadata || {});
          return data;
        }
        return {};
      } catch (err) {
        console.error('[TransactionMatchingStore]: fetchBotDetails error', err);
        commit('sbError', err?.response?.data?.message || err?.message || err || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Load bot details with pid and bid
     */
    async loadBotDetails({ commit }, { pid, bid }) {
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.LOAD_BOT_DETAILS}/${pid}/b/${bid}`);
        if (response.data.status === 'success' && response.data.data.length > 0) {
          commit('SET_BOT_DETAILS', response.data.data[0] || {});
          return response.data.data[0] || {};
        }
        return {};
      } catch (err) {
        console.error('[TransactionMatchingStore]: loadBotDetails error', err);
        commit('sbError', err.message || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Get bot id by name
     */
    async fetchBotIdByName({ commit }, { names }) {
      try {
        const response = await $axios.post(TM_API_ENDPOINTS.GET_BOT_ID_BY_NAME, { data: names });
        if (response.data.status === 'success') {
          const id = response.data.data[0]?._id || '';
          commit('SET_BOT_ID', id);
          return id;
        }
        return '';
      } catch (err) {
        console.error('[TransactionMatchingStore]: fetchBotIdByName error', err);
        throw err;
      }
    },

    /**
     * Fetch analysis form data (types, file types, results)
     */
    async fetchFormData({ commit }) {
      try {
        const response = await $axios.post(TM_API_ENDPOINTS.GET_FORM_DATA);
        if (response.data.status === 'success') {
          const data = {
            analysisTypeItems: response.data.data.analysisTypeItems || [],
            fileTypeItems: response.data.data.fileTypeItems || [],
            results: response.data.data.results || [],
          };
          commit('SET_FORM_DATA', data);
          return data;
        }
        throw new Error(response.data.message || 'Failed to fetch form data');
      } catch (err) {
        console.error('[TransactionMatchingStore]: fetchFormData error', err);
        commit('sbError', err?.response?.data?.message || err?.message || 'Failed to load analysis form data', { root: true });
        throw err;
      }
    },

    /**
     * Fetch or trigger pending payouts report
     * @param {Object} payload - { bpid, reportDate, page?, limit? }
     * @returns {Promise<Object>} - { reportStatus, records?, totalCount?, page?, limit?, totalPages? }
     */
    async fetchPendingPayoutsReport(_ctx, { collection, reportDate, page = 1, limit = 50, client, tmaid, bpid, forceRefresh = false }) {
      try {
        const response = await $axios.post(
          `${TM_API_ENDPOINTS.GET_PENDING_PAYOUTS}/${encodeURIComponent(collection)}`,
          { reportDate, pagination: { page, limit }, client, tmaid, bpid, forceRefresh }
        );
        if (response.data.status === 'success') {
          return response.data.data;
        }
        throw new Error(response.data.message || 'Failed to fetch pending payouts report');
      } catch (err) {
        console.error('[TransactionMatchingStore]: fetchPendingPayoutsReport error', err);
        throw err;
      }
    },

    /**
     * Fetch all TM analyses for a project
     */
    async fetchAnalysisList({ commit }, { pid }) {
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.GET_TM_ANALYSIS_DETAILS}/${pid}`);
        if (response.data.status === 'success') {
          commit('SET_ANALYSIS_LIST', response.data.data || []);
          return response.data.data || [];
        }
        return [];
      } catch (err) {
        console.error('[TransactionMatchingStore]: fetchAnalysisList error', err);
        throw err;
      }
    },

    /**
     * Add a new TM analysis
     */
    async addAnalysis({ commit }, { data }) {
      try {
        const response = await $axios.post(TM_API_ENDPOINTS.ADD_TM_ANALYSIS, { data });
        if (response.data.status === 'success') {
          commit('sbSuccess', 'Transaction Matching Added Successfully', { root: true });
          return { status: 'success' };
        } else {
          if (response.data.data?.length > 0 && response.data.data[0].index0) {
            return { status: 'validation_error', errors: response.data.data[0].index0 };
          }
          throw new Error(response.data.message || 'Error Adding Transaction Matching');
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: addAnalysis error', err);
        commit('sbError', err.response?.data?.message || err?.message || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Update an existing TM analysis
     */
    async updateAnalysis({ commit }, { id, data }) {
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.UPDATE_TM_ANALYSIS}/${id}`, { data });
        if (response.data.status === 'success') {
          commit('sbSuccess', 'Transaction Matching Updated Successfully', { root: true });
          return { status: 'success' };
        }
        throw new Error(response.data.message || 'Error Updating Transaction Matching');
      } catch (err) {
        console.error('[TransactionMatchingStore]: updateAnalysis error', err);
        commit('sbError', err.response?.data?.message || err?.message || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Import a TM analysis from another cycle
     */
    async importAnalysis({ commit }, { data }) {
      try {
        const response = await $axios.post(TM_API_ENDPOINTS.IMPORT_TM_ANALYSIS, { data });
        if (response.data.status === 'success') {
          commit('sbSuccess', 'Analysis Imported Successfully', { root: true });
          return { status: 'success' };
        } else {
          if (response.data.data?.length > 0 && response.data.data[0].index0) {
            return { status: 'validation_error', errors: response.data.data[0].index0 };
          }
          throw new Error(response.data.message || 'Error Adding Import Analysis');
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: importAnalysis error', err);
        commit('sbError', err.message || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Resync an imported TM analysis from its source
     */
    async resyncAnalysis({ commit }, { id }) {
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.RESYNC_TM_ANALYSIS}/${id}`, {});
        if (response.data.status === 'success') {
          commit('sbSuccess', 'Resync initiated successfully', { root: true });
          return { status: 'success' };
        }
        throw new Error(response.data.message || 'Error initiating resync');
      } catch (err) {
        console.error('[TransactionMatchingStore]: resyncAnalysis error', err);
        commit('sbError', err.response?.data?.message || err?.message || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Load TM analysis config (loadTransactionMatchingAnalysis)
     */
    async loadAnalysisConfig({ commit }, { tmaid, pid, bid }) {
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.LOAD_TM_ANALYSIS}/${tmaid}`, {
          data: { pid, bid },
        });
        if (response.data.status === 'success' && response.data.data.length > 0) {
          return response.data.data[0] || {};
        }
        return {};
      } catch (err) {
        console.error('[TransactionMatchingStore]: loadAnalysisConfig error', err);
        commit('sbError', err.message || 'Unknown error!', { root: true });
        throw err;
      }
    }
  }
};
